import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { PublicClientApplication, EventType } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import './index.css'
import App from './App.tsx'
import { msalConfig } from './auth/msal-config'
import { setRuntimeMsalInstance } from './auth/runtimeToken'
import { AppErrorBoundary } from './components/AppErrorBoundary'
import { setHubMsalInstance } from './hooks/hubConnection'
import { logger } from '@/lib/logger'

/**
 * CRITICAL: Initialize MSAL outside component tree to prevent re-instantiation
 * This prevents issues with React Router and state management
 */
const msalInstance = new PublicClientApplication(msalConfig)

// Provide MSAL instance to SignalR hub for authenticated WebSocket connections
setRuntimeMsalInstance(msalInstance)
setHubMsalInstance(msalInstance)

// Handle redirect promise and initialize MSAL
msalInstance.initialize().then(() => {
  // Handle redirect response (after login from Microsoft)
  msalInstance.handleRedirectPromise().then((response) => {
    if (response) {
      // User just logged in via redirect
      const account = response.account
      msalInstance.setActiveAccount(account)
    }
    
    // Set active account from cache if available
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0])
    }

    // Listen for sign-in events to set active account
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const account = (event.payload as { account: any }).account
        msalInstance.setActiveAccount(account)
      }
    })

    // Render app after MSAL initialization
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <AppErrorBoundary>
            <App />
          </AppErrorBoundary>
        </MsalProvider>
      </StrictMode>,
    )
  }).catch((error) => {
    logger.error('MSAL', 'Redirect error', error)
    
    // Still render app even if redirect handling fails
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <AppErrorBoundary>
            <App />
          </AppErrorBoundary>
        </MsalProvider>
      </StrictMode>,
    )
  })
})
